import { millis } from '../../Duration/dist/effect-Duration.esm.js';
import { dual } from '../../Function/dist/effect-Function.esm.js';
import { none, some } from '../../Option/dist/effect-Option.esm.js';

/** @internal */
const IntervalSymbolKey = "effect/ScheduleInterval";

/** @internal */
const IntervalTypeId = /*#__PURE__*/Symbol.for(IntervalSymbolKey);

/** @internal */
const empty = {
  [IntervalTypeId]: IntervalTypeId,
  startMillis: 0,
  endMillis: 0
};

/** @internal */
const make = (startMillis, endMillis) => {
  if (startMillis > endMillis) {
    return empty;
  }
  return {
    [IntervalTypeId]: IntervalTypeId,
    startMillis,
    endMillis
  };
};

/** @internal */
const lessThan = /*#__PURE__*/dual(2, (self, that) => min(self, that) === self);

/** @internal */
const min = /*#__PURE__*/dual(2, (self, that) => {
  if (self.endMillis <= that.startMillis) return self;
  if (that.endMillis <= self.startMillis) return that;
  if (self.startMillis < that.startMillis) return self;
  if (that.startMillis < self.startMillis) return that;
  if (self.endMillis <= that.endMillis) return self;
  return that;
});

/** @internal */
const max = /*#__PURE__*/dual(2, (self, that) => min(self, that) === self ? that : self);

/** @internal */
const isEmpty = self => {
  return self.startMillis >= self.endMillis;
};

/** @internal */
const isNonEmpty = self => {
  return !isEmpty(self);
};

/** @internal */
const intersect = /*#__PURE__*/dual(2, (self, that) => {
  const start = Math.max(self.startMillis, that.startMillis);
  const end = Math.min(self.endMillis, that.endMillis);
  return make(start, end);
});

/** @internal */
const size = self => {
  return millis(self.endMillis - self.startMillis);
};

/** @internal */
const union = /*#__PURE__*/dual(2, (self, that) => {
  const start = Math.max(self.startMillis, that.startMillis);
  const end = Math.min(self.endMillis, that.endMillis);
  return start < end ? none() : some(make(start, end));
});

/** @internal */
const after = startMilliseconds => {
  return make(startMilliseconds, Number.POSITIVE_INFINITY);
};

/** @internal */
const before = endMilliseconds => {
  return make(Number.NEGATIVE_INFINITY, endMilliseconds);
};

export { IntervalTypeId, after, before, empty, intersect, isEmpty, isNonEmpty, lessThan, make, max, min, size, union };
